import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import pricingService from "services/pricingService";

const SUMMARY_REFRESH_MS = 60000;
const SUMMARY_CACHE_MS = 15000;

export const productBaseId = row => {
  const value = Number(row?.product_base_id);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
};

export const matchesV4Product = (value, id, catalogId) =>
  productBaseId(value) === id &&
  Number(value?.catalog_id) === Number(catalogId);

export default function useV4Suggestions(
  rows,
  catalogId,
  queryKey,
  tableStatus,
  detailCatalogIds = [],
  snapshotKey = ""
) {
  const idsKey = useMemo(
    () =>
      [...new Set(rows.map(productBaseId).filter(Boolean))]
        .sort((a, b) => a - b)
        .join(","),
    [rows]
  );
  const binding = `${queryKey}|${catalogId}|${idsKey}`;
  const cacheKey = `${snapshotKey}|${catalogId}|${idsKey}`;
  const cached = useRef(null);
  const batchRequest = useRef(null);
  const lastRequestAt = useRef(0);
  const detailCatalogKey = [
    ...new Set(
      [catalogId, ...detailCatalogIds]
        .map(Number)
        .filter(id => Number.isSafeInteger(id) && id > 0)
    )
  ]
    .sort((a, b) => a - b)
    .join(",");
  const [batch, setBatch] = useState({});
  const [retryCount, setRetryCount] = useState(0);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState({});
  const currentDetail = useRef({});
  const detailRequest = useRef(null);
  const retry = useCallback(() => {
    cached.current = null;
    setRetryCount(value => value + 1);
  }, []);
  const retryDetail = useCallback(() => detailRequest.current?.retry(), []);
  const close = useCallback(() => setSelected(null), []);
  const open = useCallback(
    row => {
      const id = productBaseId(row);
      if (id) setSelected({ id, name: row.nome_produto, binding });
    },
    [binding]
  );

  useEffect(() => {
    if (!idsKey || tableStatus !== "success" || !catalogId) return undefined;
    if (cached.current?.key === cacheKey && Date.now() < cached.current.until) {
      setBatch({ ...cached.current.batch, binding });
      return undefined;
    }
    const controller = new AbortController();
    let active = true;
    batchRequest.current = controller;
    lastRequestAt.current = Date.now();
    setBatch(previous => ({
      binding,
      status: "loading",
      results: previous.binding === binding ? previous.results || {} : {}
    }));
    pricingService
      .getV4Suggestions(
        idsKey.split(",").map(Number),
        catalogId,
        controller.signal
      )
      .then(({ data }) => {
        if (!active) return;
        if (!Array.isArray(data?.results))
          throw new Error("Invalid V4 response");
        const requested = new Set(idsKey.split(",").map(Number));
        const results = {};
        data.results.forEach(item => {
          const id = productBaseId(item);
          if (requested.has(id) && matchesV4Product(item, id, catalogId))
            results[id] = item;
        });
        const completed = { binding, status: "ready", results };
        const expiry = data.results
          .map(item => Date.parse(item.expires_at))
          .filter(Number.isFinite);
        cached.current = {
          key: cacheKey,
          until: Math.min(Date.now() + SUMMARY_CACHE_MS, ...expiry),
          batch: completed
        };
        setBatch(completed);
      })
      .catch(() => {
        if (active)
          setBatch(previous => ({
            binding,
            status: "error",
            results: previous.binding === binding ? previous.results || {} : {}
          }));
      })
      .finally(() => {
        if (batchRequest.current === controller) batchRequest.current = null;
      });
    return () => {
      active = false;
      controller.abort();
      if (batchRequest.current === controller) batchRequest.current = null;
    };
  }, [binding, cacheKey, idsKey, catalogId, retryCount, tableStatus]);

  useEffect(() => {
    if (!idsKey || tableStatus !== "success" || !catalogId) return undefined;
    const refreshIfDue = () => {
      if (
        document.visibilityState === "hidden" ||
        batchRequest.current ||
        Date.now() - lastRequestAt.current < SUMMARY_CACHE_MS
      )
        return;
      lastRequestAt.current = Date.now();
      retry();
    };
    const timer = window.setInterval(refreshIfDue, SUMMARY_REFRESH_MS);
    document.addEventListener("visibilitychange", refreshIfDue);
    window.addEventListener("focus", refreshIfDue);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshIfDue);
      window.removeEventListener("focus", refreshIfDue);
    };
  }, [binding, idsKey, tableStatus, catalogId, retry]);

  const currentSelection =
    selected?.binding === binding && tableStatus === "success"
      ? {
          ...selected,
          row: rows.find(row => productBaseId(row) === selected.id)
        }
      : null;
  const selectedId = currentSelection?.id;
  const selectedExecutionId =
    batch.binding === binding
      ? batch.results?.[selectedId]?.execution_id
      : null;
  const selectedReference =
    batch.binding === binding ? batch.results?.[selectedId] : null;
  const referenceRevision = JSON.stringify([
    selectedReference?.reference_retained,
    selectedReference?.stale,
    selectedReference?.selection_allowed,
    selectedReference?.expires_at,
    selectedReference?.latest_attempt,
    selectedReference?.catalog_baseline_changed
  ]);
  const referenceBinding = `${binding}|${detailCatalogKey}|${
    selectedExecutionId || ""
  }`;
  const detailBinding = `${referenceBinding}|${referenceRevision}`;
  useEffect(() => {
    if (!selectedId) return undefined;
    const controller = new AbortController();
    let active = true;
    const previous = currentDetail.current;
    const preserved =
      selectedExecutionId &&
      previous.referenceBinding === referenceBinding &&
      previous.id === selectedId &&
      previous.data?.execution_id === selectedExecutionId &&
      matchesV4Product(previous.data, selectedId, catalogId)
        ? previous.catalogs || {}
        : {};
    let catalogs = Object.fromEntries(
      detailCatalogKey.split(",").map(value => {
        const existing = preserved[value]?.data;
        const data =
          existing && Number(value) === Number(catalogId)
            ? {
                ...existing,
                stale: selectedReference.stale,
                selection_allowed: selectedReference.selection_allowed,
                expires_at: selectedReference.expires_at,
                reference_retained: selectedReference.reference_retained,
                latest_attempt: selectedReference.latest_attempt,
                catalog_baseline_changed:
                  selectedReference.catalog_baseline_changed
              }
            : existing;
        return [
          value,
          data
            ? { status: "ready", data, refreshStatus: "loading" }
            : { status: "loading" }
        ];
      })
    );
    const updateDetail = () => {
      const next = {
        binding: detailBinding,
        referenceBinding,
        id: selectedId,
        ...catalogs[Number(catalogId)],
        catalogs
      };
      currentDetail.current = next;
      setDetail(next);
    };
    updateDetail();
    const loadCatalog = async value => {
      const catalog = Number(value);
      let result;
      try {
        const { data } = await pricingService.getV4Suggestion(
          selectedId,
          catalog,
          controller.signal
        );
        if (!matchesV4Product(data, selectedId, catalog))
          throw new Error("Invalid V4 binding");
        result = { status: "ready", data };
      } catch (_) {
        result = catalogs[catalog]?.data
          ? { ...catalogs[catalog], refreshStatus: "error" }
          : { status: "error" };
      }
      if (!active) return;
      catalogs = { ...catalogs, [catalog]: result };
      updateDetail();
    };
    const request = {
      retry: () => {
        if (!active) return;
        const failed = Object.keys(catalogs).filter(
          value =>
            catalogs[value].status === "error" ||
            catalogs[value].refreshStatus === "error"
        );
        if (!failed.length) return;
        catalogs = {
          ...catalogs,
          ...Object.fromEntries(
            failed.map(value => [
              value,
              catalogs[value].data
                ? { ...catalogs[value], refreshStatus: "loading" }
                : { status: "loading" }
            ])
          )
        };
        updateDetail();
        failed.forEach(loadCatalog);
      }
    };
    detailRequest.current = request;
    detailCatalogKey.split(",").forEach(loadCatalog);
    return () => {
      active = false;
      controller.abort();
      if (detailRequest.current === request) detailRequest.current = null;
    };
  }, [detailBinding, detailCatalogKey, selectedId, catalogId]);

  return {
    results:
      batch.binding === binding && tableStatus === "success"
        ? batch.results || {}
        : {},
    status:
      tableStatus !== "success" || batch.binding !== binding
        ? "loading"
        : batch.status,
    selected: currentSelection,
    detail:
      detail.referenceBinding === referenceBinding && detail.id === selectedId
        ? detail
        : { status: "loading" },
    open,
    close,
    retry,
    retryDetail
  };
}
